/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br."Knowledge for Decisions (K4D)" and the slogan"Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from'react';
import { cn } from'@/shared/ui/utils/tailwind';

export type ActionIconProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  variant?:'default' |'filled' |'light' |'outline' |'subtle' |'transparent';
  size?: number | string;
  color?: string;
  loading?: boolean;
  component?: React.ElementType;
  children: React.ReactNode;
  className?: string;
};

/**
 * ActionIcon component with Tailwind styling
 * Replaces Mantine ActionIcon
 */
export const ActionIcon = React.forwardRef<HTMLElement, ActionIconProps>(
  ({ variant ='default', size = 36, color, loading, component, children, className, ...props }, ref) => {
    const Element = component ||'button';
    const sizeMap: Record<string, number> = { xs: 24, sm: 30, md: 36, lg: 42, xl: 48 };
    const resolvedSize = typeof size ==='string' ? (sizeMap[size] ?? 36) : size;
    const sizeValue =`${resolvedSize}px`;
    
    const variantClasses = {
      default:'bg-transparent hover:bg-[var(--surface-hover)]',
      filled:'bg-[var(--seed-base)] text-white hover:bg-[var(--button-primary-hover-bg)]',
      light:'bg-[var(--surface-secondary)] hover:bg-[var(--surface-hover)]',
      outline:'bg-transparent border border-[var(--border-medium)] hover:bg-[var(--surface-hover)]',
      subtle:'bg-transparent hover:bg-[var(--surface-hover)]',
      transparent:'bg-transparent',
    };

    return (
      <Element
        ref={ref as any}
        type={Element ==='button' ?'button' : undefined}
        disabled={loading}
        className={cn(
          'inline-flex items-center justify-center rounded transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--seed-base)] focus-visible:ring-offset-2',
          'disabled:pointer-events-none disabled:opacity-50',
          variantClasses[variant],
          className
        )}
        style={{
          width: sizeValue,
          height: sizeValue,
          minWidth: sizeValue,
          minHeight: sizeValue,
        }}
        {...props}
      >
        {loading ? (
          <svg
            className="animate-spin h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        ) : (
          children
        )}
      </Element>
    );
  }
);

ActionIcon.displayName ='ActionIcon';

export default ActionIcon;
